import type { ApiResponse } from '../types';
import { useAuthStore } from '../stores/auth-store';

const BASE_URL = '/api';

class ApiClient {
  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    headers?: Record<string, string>,
  ): Promise<{ data: T; meta: ApiResponse<T>['meta'] }> {
    const res = await fetch(`${BASE_URL}${path}`, {
      method,
      credentials: 'include',
      headers: {
        ...(body && !headers?.['Content-Type'] ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
    });

    if (res.status === 401) {
      useAuthStore.getState().logout();
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }

    const json = (await res.json()) as ApiResponse<T>;

    if (!json.success) {
      throw new Error(json.error ?? 'Request failed');
    }

    return { data: json.data as T, meta: json.meta };
  }

  async get<T>(path: string): Promise<{ data: T; meta: ApiResponse<T>['meta'] }> {
    return this.request<T>('GET', path);
  }

  async post<T>(path: string, body?: unknown): Promise<{ data: T; meta: ApiResponse<T>['meta'] }> {
    return this.request<T>('POST', path, body);
  }

  async put<T>(path: string, body?: unknown): Promise<{ data: T; meta: ApiResponse<T>['meta'] }> {
    return this.request<T>('PUT', path, body);
  }

  async patch<T>(path: string, body?: unknown): Promise<{ data: T; meta: ApiResponse<T>['meta'] }> {
    return this.request<T>('PATCH', path, body);
  }

  async delete<T>(path: string): Promise<{ data: T; meta: ApiResponse<T>['meta'] }> {
    return this.request<T>('DELETE', path);
  }

  async upload<T>(path: string, formData: FormData): Promise<{ data: T; meta: ApiResponse<T>['meta'] }> {
    const res = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (res.status === 401) {
      useAuthStore.getState().logout();
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }

    const json = (await res.json()) as ApiResponse<T>;
    if (!json.success) throw new Error(json.error ?? 'Upload failed');
    return { data: json.data as T, meta: json.meta };
  }
}

export const api = new ApiClient();
